import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, Eye, EyeOff, User, Briefcase, Check } from 'lucide-react';
import CourtPlanLogo from '../components/CourtPlanLogo';

export default function LoginPage() {
  const location = useLocation();
  const [isRegister, setIsRegister] = useState(Boolean(location.state?.register));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [officeName, setOfficeName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [showResend, setShowResend] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users away from /login
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const axios = (await import('axios')).default;
        const API = process.env.REACT_APP_BACKEND_URL + '/api';
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (alive && data?.id) navigate('/dashboard', { replace: true });
      } catch {
        /* not logged in; stay */
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  const formatError = (detail) => {
    if (!detail) return 'Κάτι πήγε στραβά. Δοκιμάστε ξανά.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setShowResend(false);
    setLoading(true);
    try {
      if (isRegister) {
        const data = await register(email, name, officeName);
        if (data?.setup_required || data?.verification_required) {
          setIsRegister(false);
          setInfo(`Σας στείλαμε email στο ${data.email}. Ανοίξτε τον σύνδεσμο, ορίστε τον κωδικό σας και ο λογαριασμός σας ενεργοποιείται αμέσως (ελέγξτε και τα ανεπιθύμητα).`);
          return;
        }
      } else {
        await login(email, password);
      }
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail === 'SETUP_PENDING') {
        setError('Ο λογαριασμός σας δεν έχει ενεργοποιηθεί ακόμη. Ανοίξτε το email που σας στείλαμε και ορίστε τον κωδικό σας.');
        setShowResend(true);
      } else if (err.response?.status === 403 && detail === 'EMAIL_NOT_VERIFIED') {
        setError('Το email σας δεν έχει επιβεβαιωθεί ακόμη. Ελέγξτε τα εισερχόμενά σας για τον σύνδεσμο ενεργοποίησης.');
        setShowResend(true);
      } else {
        setError(formatError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      const axios = (await import('axios')).default;
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      await axios.post(`${API}/auth/resend-verification`, { email });
      setError('');
      setShowResend(false);
      setInfo(`Στάλθηκε νέο email επιβεβαίωσης στο ${email}.`);
    } catch {
      setError('Η αποστολή απέτυχε. Δοκιμάστε ξανά σε λίγο.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f2e7] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <CourtPlanLogo size="md" />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          {/* Tabs */}
          <div className="flex mb-6 bg-slate-100 rounded-lg p-1" data-testid="auth-tabs">
            <button
              type="button"
              onClick={() => { setIsRegister(false); setError(''); }}
              className={`flex-1 h-9 text-sm font-medium rounded-md transition-all ${
                !isRegister ? 'bg-white text-red-700 shadow-sm' : 'text-slate-500'
              }`}
              data-testid="tab-login"
            >
              ΣΥΝΔΕΣΗ
            </button>
            <button
              type="button"
              onClick={() => { setIsRegister(true); setError(''); }}
              className={`flex-1 h-9 text-sm font-medium rounded-md transition-all ${
                isRegister ? 'bg-white text-red-700 shadow-sm' : 'text-slate-500'
              }`}
              data-testid="tab-register"
            >
              ΕΓΓΡΑΦΗ
            </button>
          </div>

          {isRegister && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid="register-free-banner">
              <p className="text-sm font-bold text-red-700 text-center">Η εγγραφή είναι ΔΩΡΕΑΝ</p>
              <p className="text-sm font-semibold text-slate-800 text-center mb-2">Λαμβάνεις 100 δωρεάν μονάδες</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center justify-center gap-1.5"><Check className="w-3.5 h-3.5 text-red-700 shrink-0" />5 μονάδες για κάθε νέα καταχώρηση</li>
                <li className="flex items-center justify-center gap-1.5"><Check className="w-3.5 h-3.5 text-red-700 shrink-0" />5 μονάδες ανά χρήση του AI Organiser</li>
                <li className="flex items-center justify-center gap-1.5"><Check className="w-3.5 h-3.5 text-red-700 shrink-0" />Όλες οι υπόλοιπες ενέργειες δωρεάν</li>
              </ul>
            </div>
          )}

          {info && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800" data-testid="auth-info">
              {info}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="auth-error">
              {error}
              {showResend && (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={loading}
                  className="block mt-2 text-sm font-semibold text-red-700 underline hover:text-red-900 disabled:opacity-50"
                  data-testid="resend-verification-btn"
                >
                  Επαναποστολή email επιβεβαίωσης
                </button>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            {isRegister && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Ονοματεπώνυμο</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="π.χ. Μαρία Παπαδοπούλου"
                      className="w-full h-11 pl-10 pr-4 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700"
                      required
                      autoComplete="off"
                      data-lpignore="true"
                      data-testid="register-name-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Δικηγορικό Γραφείο <span className="text-slate-400 text-xs">(προαιρετικό)</span>
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={officeName}
                      onChange={(e) => setOfficeName(e.target.value)}
                      placeholder="π.χ. Δικηγορικό Γραφείο Παπαδοπούλου"
                      className="w-full h-11 pl-10 pr-4 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700"
                      autoComplete="off"
                      data-lpignore="true"
                      data-testid="register-office-input"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {isRegister ? 'Email' : 'Email ή όνομα χρήστη'}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type={isRegister ? 'email' : 'text'}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={isRegister ? 'you@example.com' : 'π.χ. admin ή you@example.com'}
                  className="w-full h-11 pl-10 pr-4 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700"
                  required
                  autoComplete="off"
                  data-lpignore="true"
                  data-testid={isRegister ? 'register-email-input' : 'login-email-input'}
                />
              </div>
            </div>

            {!isRegister && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Κωδικός</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Ο κωδικός σας"
                  className="w-full h-11 pl-10 pr-12 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-700 focus:border-red-700"
                  required
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            )}

            {isRegister && (
              <p className="text-xs text-slate-500" data-testid="register-password-note">
                Θα λάβετε email με σύνδεσμο για να ορίσετε τον κωδικό σας και να ενεργοποιήσετε τον λογαριασμό.
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-red-700 hover:bg-red-800 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
              data-testid={isRegister ? 'register-submit-btn' : 'login-submit-button'}
            >
              {loading ? '...' : isRegister ? 'ΔΗΜΙΟΥΡΓΙΑ ΛΟΓΑΡΙΑΣΜΟΥ' : 'ΣΥΝΔΕΣΗ'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
